import React, { useMemo, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Button, Box, Grid, Stack } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import FinishedGoodsViewModal from 'components/generic/modals/dialog-modal/FinishedGoodsViewModal'
import Loading from 'components/generic/Loading'
import { useGetPackingListsQuery } from 'services/packingList'
import { COMMON_STATUSES } from 'utils/constants'
import { formatNumber } from 'utils/helpers'
import DialogModal from '.'

const PackingListsModal = ({
  onClose,
  apiRef: materialsApiRef,
  packingLists,
  setPackingLists,
  priceSheetsData,
  onUpdateMaterial,
  ...restProps
}) => {
  const apiRef = useGridApiRef()
  const [selectedPackingListIds, setSelectedPackingListIds] = useState([])
  const [cachedPackingListData, setCachedPackingListData] = useState({})
  const [selectedFinishedGoods, setSelectedFinishedGoods] = useState([])
  const [selectedPackingListId, setSelectedPackingListId] = useState()
  const [openFinishedGoodsViewModal, setOpenFinishedGoodsViewModal] = useState(false)

  const { data: packingListsData, isLoading } = useGetPackingListsQuery({
    filter: JSON.stringify({
      statuses: [COMMON_STATUSES.AVAILABLE.id],
    }),
  })

  const addNewMaterialWithPL = pl => {
    const packingList = cachedPackingListData[pl]

    packingList.fgs?.forEach(fg => {
      const firstPriceSheetEntry = priceSheetsData?.Items?.[0]?.entriesObj?.find(
        entry => entry.materialTypeId === fg.materialTypeId,
      )

      materialsApiRef.current.updateRows([
        {
          id: fg.id,
          orderEntryId: firstPriceSheetEntry?.id,
          isNew: true,
          code: {
            materialName: fg.materialName,
            materialCode: fg.materialCode,
            materialTypeId: fg.materialTypeId,
            orderEntryId: firstPriceSheetEntry?.id,
          },
          plId: packingList.plId,
          plTag: packingList.plTag,
          fgId: fg.fgTag,
          fgType: fg.package,
          itemCount: fg.itemCount,
          yardGross: fg.gross,
          yardTare: fg.tare,
          yardNet: fg.netWeight,
        },
      ])
      materialsApiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowsCount() - 1,
      })
    })
  }

  const onAddPackingLists = useCallback(
    pls => {
      onClose()

      pls.forEach(pl => addNewMaterialWithPL(pl))
      onUpdateMaterial()
    },
    [cachedPackingListData],
  )

  useEffect(() => {
    if (packingListsData) {
      const { entriesObj } = priceSheetsData?.Items?.[0] || {}
      const filteredPackingListList = packingListsData?.Items.filter(
        pl =>
          !pl.outboundTicketId &&
          (entriesObj
            ? pl.finishedGoods.some(fg => entriesObj.some(entry => fg.materialTypeId === entry.materialTypeId))
            : true),
      ).map(pl => ({
        id: pl?.id,
        plId: pl?.id,
        plTag: pl?.tag,
        date: new Date(pl?.createdDate)?.toDateString(),
        materialName:
          (pl?.finishedGoods && pl?.finishedGoods.length > 0 && pl?.finishedGoods[0]?.materialType?.commonName) || '',
        materialCode:
          (pl?.finishedGoods && pl?.finishedGoods.length > 0 && pl?.finishedGoods[0]?.materialType?.code) || '',
        netWeight: pl?.net,
        fgCount: pl?.finishedGoods?.length,
        fgs:
          pl?.finishedGoods && pl?.finishedGoods?.length > 0
            ? pl?.finishedGoods?.map(fg => ({
                id: fg?.id,
                fgTag: fg?.tag,
                plTag: pl?.tag,
                plDate: new Date(pl?.createdDate)?.toDateString(),
                materialCode: fg?.materialType?.code,
                materialName: fg?.materialType?.commonName,
                materialTypeId: fg?.materialType?.id,
                package: fg?.type,
                itemCount: fg?.itemCount,
                gross: fg?.weight?.gross,
                tare: fg?.weight?.tare,
                netWeight: fg?.weight?.net,
              }))
            : [],
      }))
      setPackingLists(filteredPackingListList)
    }
  }, [packingListsData])

  useEffect(() => {
    if (packingLists.length > 0 && selectedPackingListId !== '') {
      const packingListItem = packingLists.filter(pl => pl.id === selectedPackingListId)[0]
      const finishedGoods = packingListItem?.fgs
      setSelectedFinishedGoods(finishedGoods)

      if (finishedGoods?.length > 0) {
        setOpenFinishedGoodsViewModal(true)
      }
      setSelectedPackingListId('')
    }
  }, [selectedPackingListId])

  const fgColumns = useMemo(
    () => [
      {
        field: 'plTag',
        headerName: 'PL #',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'materialName',
        headerName: 'Materials',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'netWeight',
        headerName: 'Net Weight',
        flex: 1,
        editable: false,
        sortable: false,
        valueFormatter: params => `${formatNumber(params.value)}`,
      },
      {
        field: 'fgCount',
        headerName: '# of FGs',
        flex: 1,
        type: 'number',
        editable: false,
        headerAlign: 'center',
        sortable: false,
      },
      {
        field: 'view',
        headerName: 'View',
        sortable: false,
        renderCell: params => {
          const onClick = e => {
            e.stopPropagation() // don't select this row after clicking

            setSelectedPackingListId(params.row.id)
          }

          return (
            <Button onClick={onClick}>
              <VisibilityIcon />
            </Button>
          )
        },
      },
    ],
    [],
  )

  if (isLoading) return <Loading />
  return (
    <Box>
      <DialogModal {...restProps} onClose={onClose} onSave={() => onAddPackingLists(selectedPackingListIds)}>
        <Stack sx={{ height: 450, overflow: 'scroll' }}>
          <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
            <DataGridWithSearch
              density="compact"
              height="auto"
              rows={packingLists}
              columns={fgColumns}
              apiRef={apiRef}
              editMode="row"
              checkboxSelection
              disableColumnMenu
              textSearchPlaceholder="Search by Packing List or Material"
              onSelectionModelChange={ids => {
                setSelectedPackingListIds(ids)
                const newPackingListsData = {}
                ids.forEach(id => {
                  if (!cachedPackingListData[id]) {
                    newPackingListsData[id] = packingLists.find(packingList => packingList.id === id)
                  }
                })
                setCachedPackingListData(prevState => ({
                  ...prevState,
                  ...newPackingListsData,
                }))
              }}
              selectionModel={selectedPackingListIds}
            />
          </Grid>
        </Stack>
      </DialogModal>
      <FinishedGoodsViewModal
        buttonTitle="CLOSE"
        finishedGoodsList={selectedFinishedGoods}
        open={openFinishedGoodsViewModal}
        onSave={() => setOpenFinishedGoodsViewModal(false)}
      />
    </Box>
  )
}

PackingListsModal.propTypes = {
  ...DialogModal.propTypes,
  packingLists: PropTypes.arrayOf(PropTypes.object),
}

PackingListsModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'Add Existing Packing Lists',
  packingLists: [],
}

export default PackingListsModal
